import { useAgentSidebar, useAgentWorkspace } from "./AgentSidebarContext";
import { ASSISTANT_RAIL_HEIGHT, ASSISTANT_RAIL_Z_INDEX } from "./ChalkAssistantPanel";
import {
  ASSISTANT_MAX_OPEN_CHATS,
  findBlankAssistantChat,
  isAtAssistantChatLimit,
  isBlankAssistantChat,
} from "./assistant-workspace-state";
import { ButtonV2, IconButtonV2 } from "@/components/interactive/ButtonV2";
import { Icon } from "@/components/icons/Icon";
import { TooltipV2 } from "@/components/TooltipV2";
import {
  IconButtonDropdownMenuV2,
  IconButtonDropdownMenuItemV2,
} from "@/components/interactive/IconButtonDropdownMenuV2";
import { MenuSectionV2 } from "@/components/interactive/MenuContainerV2";
import { MenuItemV2 } from "@/components/interactive/MenuItemV2";
import {
  createConversation,
  listConversations,
} from "@/gen_src/proto/chalk/agent/v1/conversation-AgentConversationService_connectquery";
import {
  PermissionRequiredButtonV2,
  PermissionRequiredIconButtonV2,
} from "@/components/permission/PermissionRequiredButton";
import { useQuery } from "@connectrpc/connect-query";
import { keepPreviousData } from "@tanstack/react-query";
import { useDebouncedValue } from "@/utils/hooks/form/use-debounce";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { AmplitudeEvent, useAmplitudeTrack } from "@/utils/amplitude";
import { Routes } from "@/utils/routes";
import { timestampToDate } from "@/utils/proto-utils";
import { formatRelativeOrDate } from "@/utils/relative-date";
import { classNames } from "@/utils/classnames";
import { MOTION_DURATION_CLASS, MOTION_DURATION_MS } from "@/utils/motion";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AgentConversationSortColumn,
  AgentConversationSortOrder,
} from "@/gen_src/proto/chalk/agent/v1/conversation_pb";

export const ChalkAssistantRail: React.FC<{
  historyContent?: React.ReactNode;
  loading?: boolean;
}> = ({ historyContent, loading = false }) => {
  const workspace = useAgentWorkspace();
  const ref = useRef<HTMLDivElement>(null);
  const setRailBounds = workspace?.setRailBounds;
  useEffect(() => {
    const element = ref.current;
    if (element == null || setRailBounds == null) return;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      const left = Math.max(0, Math.min(window.innerWidth, rect.left));
      const width = Math.max(0, Math.min(window.innerWidth, rect.right) - left);
      setRailBounds((previous) =>
        previous?.left === left && previous.width === width ? previous : { left, width },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      setRailBounds(null);
    };
  }, [setRailBounds]);
  if (workspace == null && !loading) return null;
  return (
    <div
      ref={ref}
      role="toolbar"
      aria-label="Chalk Assistant chats"
      aria-busy={loading || undefined}
      data-testid="assistant-rail"
      data-ds-v2=""
      className="design-system-typography relative flex h-cds-40 shrink-0 items-center justify-end gap-cds-8 bg-cds-surface-active pl-cds-8 pr-cds-12 dark:bg-cds-inset-dark"
      style={{
        height: ASSISTANT_RAIL_HEIGHT,
        zIndex: ASSISTANT_RAIL_Z_INDEX,
      }}
    >
      {loading ? (
        <>
          <ButtonV2 disabled size="xs" variant="tertiary" icon="sparkles">
            Assistant
          </ButtonV2>
          <IconButtonV2
            disabled
            icon="history"
            size="xs"
            variant="tertiary"
            aria-label="Chat history"
          />
        </>
      ) : workspace != null ? (
        <ChalkAssistantRailControls workspace={workspace} historyContent={historyContent} />
      ) : null}
    </div>
  );
};

// Opens a new tab's slot with the design system's reveal (`CollapsiblePanelV2`, `SideNavV2`) run
// sideways: a 0fr -> 1fr grid column carrying opacity, so neighbouring tabs slide aside rather than
// jump. The slot clips its tab only while opening: the strip scrolls, so anything a settled tab
// paints past its box (a focus ring) would otherwise show up as strip overflow or be cut off.
const RailTabReveal: React.FC<React.PropsWithChildren<{ animate: boolean }>> = ({
  animate,
  children,
}) => {
  const [revealed, setRevealed] = useState(!animate);
  const [settled, setSettled] = useState(!animate);
  useEffect(() => {
    if (!revealed || settled) return;
    // A timer rather than transitionend, which never fires under reduced motion.
    const timeout = window.setTimeout(() => setSettled(true), MOTION_DURATION_MS.medium);
    return () => window.clearTimeout(timeout);
  }, [revealed, settled]);
  useEffect(() => {
    if (revealed) return;
    // Two frames so the collapsed column is styled before it opens; one can land in the same style
    // pass as the mount and skip the transition.
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setRevealed(true));
    });
    return () => cancelAnimationFrame(frame);
  }, [revealed]);
  return (
    <div
      data-revealed={revealed}
      className={classNames(
        "grid shrink-0 transition-[grid-template-columns,opacity] ease-out motion-reduce:transition-none",
        MOTION_DURATION_CLASS.medium,
        revealed ? "grid-cols-[1fr] opacity-100" : "grid-cols-[0fr] opacity-0",
      )}
    >
      <div className={classNames("min-w-0", !settled && "overflow-hidden")}>{children}</div>
    </div>
  );
};

const ChalkAssistantRailControls: React.FC<{
  workspace: NonNullable<ReturnType<typeof useAgentWorkspace>>;
  historyContent?: React.ReactNode;
}> = ({ workspace, historyContent }) => {
  const { state, focusChat, closeChat, updateChat, newChat } = workspace;
  // A blank chat has no tab until a conversation starts in it; until then "+ Assistant" is its
  // handle: it opens and focuses that window, or minimizes it if it is already open. Otherwise it
  // adds a new chat tab and opens its window in place of the open one. `aria-controls` also lets
  // the window shrink onto it.
  const blankChat = findBlankAssistantChat(state);
  const atChatLimit = blankChat == null && isAtAssistantChatLimit(state);
  // Tabs already on the rail when it mounts appear in place: the rail remounts with each page, and
  // replaying the entrance there would animate tabs the user did not just add.
  const [initialChatIds] = useState(() => new Set(state.chats.map((chat) => chat.id)));
  const track = useAmplitudeTrack();
  return (
    <>
      <div
        className="flex min-w-0 items-center gap-cds-8 overflow-x-auto no-scrollbar-gutter"
        data-assistant-tab-strip=""
      >
        {state.chats
          .filter((chat) => !isBlankAssistantChat(chat))
          .map((chat) => (
            <RailTabReveal key={chat.id} animate={!initialChatIds.has(chat.id)}>
              <div
                className={classNames(
                  "group/chat-tab relative flex shrink-0 items-center rounded-cds-6",
                  !chat.minimized && "bg-cds-surface-default dark:bg-white/[0.06]",
                )}
              >
                <TooltipV2 content={chat.title} placement="top">
                  <button
                    type="button"
                    aria-label={`${chat.minimized ? "Open" : "Minimize"} ${chat.title}`}
                    aria-pressed={!chat.minimized}
                    aria-controls={`assistant-panel-${chat.id}`}
                    className={classNames(
                      "flex h-7 max-w-[200px] items-center gap-cds-6 rounded-cds-6 px-cds-8 text-cds-sm group-hover/chat-tab:bg-black/[0.04] dark:group-hover/chat-tab:bg-white/[0.06]",
                      chat.minimized
                        ? "text-cds-ink-subtle dark:!text-zinc-400"
                        : "text-cds-ink-strong dark:!text-zinc-100",
                    )}
                    onClick={() =>
                      !chat.minimized
                        ? updateChat(chat.id, { minimized: true })
                        : focusChat(chat.id)
                    }
                  >
                    {chat.status !== "idle" && (
                      <Icon
                        name={chat.status === "error" ? "error" : "rotateCcw"}
                        size="xs"
                        className={
                          chat.status === "error"
                            ? "text-cds-error"
                            : "animate-spin motion-reduce:animate-none"
                        }
                      />
                    )}
                    <span className="min-w-0 flex-1 truncate text-left group-hover/chat-tab:[mask-image:linear-gradient(to_right,black_calc(100%_-_22px),transparent_calc(100%_-_18px))] group-has-[:focus-visible]/chat-tab:[mask-image:linear-gradient(to_right,black_calc(100%_-_22px),transparent_calc(100%_-_18px))]">
                      {chat.title}
                    </span>
                  </button>
                </TooltipV2>
                <IconButtonV2
                  size="xs"
                  variant="tertiary"
                  icon="close"
                  className="!absolute right-cds-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 transition-opacity duration-150 motion-reduce:transition-none group-hover/chat-tab:pointer-events-auto group-hover/chat-tab:opacity-100 group-has-[:focus-visible]/chat-tab:pointer-events-auto group-has-[:focus-visible]/chat-tab:opacity-100"
                  aria-label={`Close ${chat.title}`}
                  onClick={() => closeChat(chat.id)}
                />
              </div>
            </RailTabReveal>
          ))}
      </div>
      <TooltipV2
        content={`${ASSISTANT_MAX_OPEN_CHATS} chats are open. Close one to start another.`}
        placement="top"
        disabled={!atChatLimit}
        instant
      >
        {/* A disabled button takes no pointer events, so the tooltip hangs off this wrapper. */}
        <span className="inline-flex shrink-0">
          {/* Starting a chat calls CreateConversation; without that permission the button is
              disabled and explains which roles grant it. */}
          <PermissionRequiredButtonV2
            endpoint={createConversation}
            actionName="Starting an assistant chat"
            placement="top"
            size="xs"
            variant="tertiary"
            icon="sparkles"
            onClick={() => {
              if (blankChat != null && !blankChat.minimized) {
                updateChat(blankChat.id, { minimized: true });
                return;
              }
              track(AmplitudeEvent.AI_ASSISTANT_OPEN, {
                surface: "assistant_rail",
                open_chat_count: state.chats.length,
              });
              newChat();
            }}
            disabled={atChatLimit}
            className={classNames("shrink-0", atChatLimit && "pointer-events-none")}
            aria-controls={blankChat != null ? `assistant-panel-${blankChat.id}` : undefined}
            aria-expanded={blankChat != null ? !blankChat.minimized : undefined}
            aria-label={
              blankChat == null
                ? "New assistant chat"
                : blankChat.minimized
                  ? "Open new chat"
                  : "Minimize new chat"
            }
          >
            Assistant
          </PermissionRequiredButtonV2>
        </span>
      </TooltipV2>
      <IconButtonDropdownMenuV2
        trigger={
          <PermissionRequiredIconButtonV2
            endpoint={listConversations}
            actionName="Viewing chat history"
            placement="top"
            icon="history"
            size="xs"
            variant="tertiary"
            aria-label="Chat history"
            aria-haspopup="menu"
          />
        }
        width={320}
        closeOnMouseLeave={false}
        floatOptions={{ placement: "top-end" }}
      >
        {historyContent ?? <AssistantHistory />}
      </IconButtonDropdownMenuV2>
    </>
  );
};

const HISTORY_ITEM_CLASS = "[&_.menu-item-v2-left-icon]:hidden";

const HISTORY_SEARCH_DEBOUNCE_MS = 300;

const AssistantHistory = () => {
  const { projectId, environmentId } = useRouteInfo();
  const sidebar = useAgentSidebar();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), HISTORY_SEARCH_DEBOUNCE_MS);
  const query = useQuery(
    listConversations,
    {
      pageSize: 20,
      search: debouncedSearch,
      sortColumn: AgentConversationSortColumn.UPDATED_AT,
      sortOrder: AgentConversationSortOrder.DESC,
    },
    { staleTime: 0, placeholderData: keepPreviousData },
  );
  const handleSearchKeyDown = useCallback<React.KeyboardEventHandler<HTMLInputElement>>((event) => {
    if (event.key !== "Escape") {
      event.stopPropagation();
    }
  }, []);
  return (
    <>
      <MenuSectionV2>
        {/* Pinned to the menu surface's scroll container. The negative margins cover the
            section's padding so rows scrolling underneath never peek out around the header. */}
        <div className="sticky top-0 z-[1] -mx-cds-3 -mt-cds-3 bg-cds-surface-default px-cds-3 pt-cds-3 dark:bg-cds-elevated-dark">
          <MenuItemV2
            variant="search"
            size="sm"
            label="Search recent chats"
            searchValue={search}
            onSearchChange={setSearch}
            onSearchKeyDown={handleSearchKeyDown}
            showBottomDivider
          />
        </div>
        {query.isPending ? (
          <div className="p-cds-8 text-cds-sm">Loading chats...</div>
        ) : query.isError ? (
          <IconButtonDropdownMenuItemV2
            label="Could not load chats. Retry"
            onClick={() => void query.refetch()}
            closeOnSelect={false}
            className={HISTORY_ITEM_CLASS}
          />
        ) : query.data?.conversations.length === 0 ? (
          <div className="p-cds-8 text-cds-sm text-cds-ink-subtle">
            {debouncedSearch ? "No chats match your search" : "No saved chats yet"}
          </div>
        ) : (
          query.data?.conversations.map((conversation) => {
            const lastActivity = conversation.updatedAt ?? conversation.createdAt;
            const subtitle =
              lastActivity != null ? formatRelativeOrDate(timestampToDate(lastActivity)) : "–";
            return (
              <IconButtonDropdownMenuItemV2
                key={conversation.id}
                variant="two-line"
                label={conversation.title || "Untitled chat"}
                subtitle={subtitle}
                onClick={() => sidebar?.requestConversation(conversation.id)}
                className={HISTORY_ITEM_CLASS}
              />
            );
          })
        )}
      </MenuSectionV2>
      {projectId != null && environmentId != null && (
        <MenuSectionV2>
          <IconButtonDropdownMenuItemV2
            label="View all conversations"
            href={Routes.project.environment.agentConversations(projectId, environmentId)}
            className={HISTORY_ITEM_CLASS}
          />
        </MenuSectionV2>
      )}
    </>
  );
};
